export interface ReportType {
  id: string;
  name: string;
  description: string;
  panelSize: number;
  specimenType: string;
  category: 'respiratory' | 'gastrointestinal' | 'urinary' | 'wound';
  pdfPath: string;
  imagePath?: string;
}

export interface PatientInfo {
  patient_id: string;
  patient_name: string;
  patient_dob: string;
  patient_gender: string;
  patient_age: string;
}

export interface CollectionInfo {
  collection_site: string;
  specimen_type: string;
  collection_date: string;
  received_date: string;
}

export interface ProviderInfo {
  provider_name: string;
  provider_location: string;
}

export interface TestResult {
  name: string;
  result: 'Detected' | 'Not Detected';
}

export interface TestDetails {
  viruses: TestResult[];
  bacteria: TestResult[];
  resistance_genes: TestResult[];
  fungi: TestResult[];
  parasites?: TestResult[];
}

export interface ReportData {
  test_id: string;
  panel_type: string;
  panel_size: number;
  patient_info: PatientInfo;
  collection_info: CollectionInfo;
  provider_info: ProviderInfo;
  test_details: TestDetails;
  analysis_results?: {
    pathogens?: {
      detected?: {
        bacteria: TestResult[];
        viruses: TestResult[];
        fungi: TestResult[];
        parasites: TestResult[];
      };
    };
    weighted_pathogens?: Array<{
      name: string;
      standardized_name: string;
      category: string;
      weight: number;
      priority_rank: number;
    }>;
    treatment?: {
      primary?: {
        drug_name: string;
        dosage: string;
        route: string;
        frequency: string;
      };
      drug_recommendations?: {
        alternatives: Array<{
          name: string;
          dosage: string;
          route: string;
          frequency: string;
        }>;
      };
      precautions?: {
        monitoring_requirements: string[];
      };
    };
  };
}

export const REPORT_TYPES: ReportType[] = [
  {
    id: 'gi_comp_112',
    name: 'GI Comprehensive Panel',
    description: 'Comprehensive gastrointestinal pathogen detection panel',
    panelSize: 112,
    specimenType: 'Fecal Swab',
    category: 'gastrointestinal',
    pdfPath: '/resources/reports/TEST_GI_COMP_112_Fecal_Swab_report.pdf'
  },
  {
    id: 'uti_ud_112',
    name: 'UTI Panel',
    description: 'Urinary tract infection pathogen detection panel',
    panelSize: 112,
    specimenType: 'Urine Swab',
    category: 'urinary',
    pdfPath: '/resources/reports/TEST_UTI_UD_112_Swab_report.pdf'
  },
  {
    id: 'rpp_sinus_112',
    name: 'Respiratory Pathogen Panel - Sinus',
    description: 'Respiratory pathogen detection for sinus infections',
    panelSize: 112,
    specimenType: 'Nasal Swab',
    category: 'respiratory',
    pdfPath: '/resources/reports/TEST_RPP_SINUS_112_Nasal_Swab_report.pdf'
  },
  {
    id: 'wnd_acute_56',
    name: 'Wound Acute Panel',
    description: 'Acute wound infection pathogen detection',
    panelSize: 56,
    specimenType: 'Wound Swab',
    category: 'wound',
    pdfPath: '/resources/reports/TEST_WND_ACUTE_56_Swab_report.pdf'
  },
  {
    id: 'wnd_comp_56',
    name: 'Wound Comprehensive Panel (56)',
    description: 'Comprehensive wound pathogen detection - 56 test panel',
    panelSize: 56,
    specimenType: 'Wound Swab',
    category: 'wound',
    pdfPath: '/resources/reports/TEST_WND_COMP_56_Swab_report.pdf'
  },
  {
    id: 'wnd_comp_112',
    name: 'Wound Comprehensive Panel (112)',
    description: 'Comprehensive wound pathogen detection - 112 test panel',
    panelSize: 112,
    specimenType: 'Wound Swab',
    category: 'wound',
    pdfPath: '/resources/reports/TEST_WND_COMP_112_Swab_report.pdf'
  },
  {
    id: 'wnd_comp_abx_56',
    name: 'Wound Comprehensive + ABX Panel (56)',
    description: 'Comprehensive wound pathogen detection with antibiotic resistance - 56 test panel',
    panelSize: 56,
    specimenType: 'Wound Swab',
    category: 'wound',
    pdfPath: '/resources/reports/TEST_WND_COMP_ABX_56_Swab_report.pdf'
  },
  {
    id: 'wnd_comp_abx_112',
    name: 'Wound Comprehensive + ABX Panel (112)',
    description: 'Comprehensive wound pathogen detection with antibiotic resistance - 112 test panel',
    panelSize: 112,
    specimenType: 'Wound Swab',
    category: 'wound',
    pdfPath: '/resources/reports/TEST_WND_COMP_ABX_112_Swab_report.pdf'
  }
];

export const getReportTypeById = (id: string): ReportType | undefined => {
  return REPORT_TYPES.find(type => type.id === id);
};

export const getReportTypesByCategory = (category: string): ReportType[] => {
  return REPORT_TYPES.filter(type => type.category === category);
}; 